import React, { useState } from 'react';
import {
  Alert,
  Button,
  Content,
  Flex,
  FlexItem,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  TextInput,
} from '@patternfly/react-core';
import { ActionsColumn, type IAction, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAdminSplaCategories } from '../../../hooks/useAdminSplaCatalog';
import type { SplaCategory, SplaCategoryUpdateRequest } from '../../../types/models/Spla';
import type { ApiResponseSplaCategory, ApiResponseSplaCategoryDeleted } from '../../../types/api/ApiResponseSpla';

const NAME_MAX = 64;

interface CategoryMutationResult {
  ok: boolean;
  /** Backend field-level error (data.name / data.sort_order), surfaced inline; null otherwise. */
  fieldError: string | null;
}

interface SplaCategoryRowProps {
  category: SplaCategory;
  onUpdate: (id: number, changes: SplaCategoryUpdateRequest) => Promise<CategoryMutationResult>;
  onDelete: (id: number) => Promise<{ ok: boolean }>;
}

// One sheet section: inline edit for name + sort order (reordering is a sort_order edit — no drag
// & drop), delete pre-disabled while any license (archived included) references it.
function SplaCategoryRow({ category, onUpdate, onDelete }: SplaCategoryRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [orderDraft, setOrderDraft] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const trimmedName = nameDraft.trim();
  const trimmedOrder = orderDraft.trim();
  const isNameValid = trimmedName.length > 0 && trimmedName.length <= NAME_MAX;
  const isOrderValid = /^\d+$/.test(trimmedOrder);
  const isUnchanged = trimmedName === category.name && Number(trimmedOrder) === category.sort_order;
  const helperError =
    serverError ??
    (trimmedName.length > NAME_MAX
      ? `Name must be at most ${NAME_MAX} characters.`
      : !isOrderValid && trimmedOrder !== ''
        ? 'Sort order must be a non-negative whole number.'
        : null);

  const deleteDisabledReason =
    category.license_count > 0
      ? `Referenced by ${category.license_count} license${category.license_count === 1 ? '' : 's'} (archived included); reassign them before deleting it.`
      : null;

  const openEditor = () => {
    setNameDraft(category.name);
    setOrderDraft(String(category.sort_order));
    setServerError(null);
    setIsEditing(true);
  };

  const save = async () => {
    if (isUnchanged) {
      setIsEditing(false);
      return;
    }
    if (!isNameValid || !isOrderValid) {
      return;
    }
    const changes: SplaCategoryUpdateRequest = {};
    if (trimmedName !== category.name) {
      changes.name = trimmedName;
    }
    if (Number(trimmedOrder) !== category.sort_order) {
      changes.sort_order = Number(trimmedOrder);
    }
    setIsSaving(true);
    setServerError(null);
    const result = await onUpdate(category.id, changes);
    setIsSaving(false);
    if (result.ok) {
      setIsEditing(false);
    } else {
      setServerError(result.fieldError);
    }
  };

  const remove = async () => {
    setIsDeleting(true);
    await onDelete(category.id);
    setIsDeleting(false);
  };

  const rowActions: IAction[] = [
    {
      title: 'Edit',
      onClick: openEditor,
      isAriaDisabled: isSaving || isDeleting,
    },
    {
      title: 'Delete',
      onClick: () => void remove(),
      isDanger: true,
      // isAriaDisabled (not isDisabled) so the tooltip's reason stays hoverable/focusable.
      isAriaDisabled: deleteDisabledReason !== null || isSaving || isDeleting,
      ...(deleteDisabledReason ? { tooltipProps: { content: deleteDisabledReason } } : {}),
    },
  ];

  return (
    <Tr>
      <Td dataLabel="Section">
        {isEditing ? (
          <Flex
            spaceItems={{ default: 'spaceItemsSm' }}
            alignItems={{ default: 'alignItemsFlexStart' }}
            flexWrap={{ default: 'nowrap' }}
          >
            <FlexItem grow={{ default: 'grow' }}>
              <TextInput
                id={`spla-category-name-${category.id}`}
                type="text"
                value={nameDraft}
                onChange={(_event, value) => {
                  setNameDraft(value);
                  if (serverError) {
                    setServerError(null);
                  }
                }}
                validated={helperError ? 'error' : 'default'}
                isDisabled={isSaving}
                aria-label={`Name for section ${category.name}`}
              />
              {helperError && (
                <HelperText>
                  <HelperTextItem variant="error">{helperError}</HelperTextItem>
                </HelperText>
              )}
            </FlexItem>
            <FlexItem>
              <TextInput
                id={`spla-category-order-${category.id}`}
                type="text"
                inputMode="numeric"
                value={orderDraft}
                onChange={(_event, value) => {
                  setOrderDraft(value);
                  if (serverError) {
                    setServerError(null);
                  }
                }}
                validated={!isOrderValid ? 'error' : 'default'}
                isDisabled={isSaving}
                aria-label={`Sort order for section ${category.name}`}
                style={{ width: '5rem' }}
              />
            </FlexItem>
            <FlexItem>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void save()}
                isLoading={isSaving}
                isDisabled={isSaving || !isNameValid || !isOrderValid || isUnchanged}
              >
                Save
              </Button>
            </FlexItem>
            <FlexItem>
              <Button variant="link" size="sm" onClick={() => setIsEditing(false)} isDisabled={isSaving}>
                Cancel
              </Button>
            </FlexItem>
          </Flex>
        ) : (
          category.name
        )}
      </Td>
      <Td dataLabel="Position">{!isEditing && category.sort_order}</Td>
      <Td dataLabel="Licenses">
        {category.license_count > 0 ? (
          `${category.license_count} license${category.license_count === 1 ? '' : 's'}`
        ) : (
          <Content component="small">Unused</Content>
        )}
      </Td>
      <Td isActionCell>{!isEditing && <ActionsColumn items={rowActions} />}</Td>
    </Tr>
  );
}

interface SplaCategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called on close if anything changed, so the grid re-fetches (a rename changes group headers). */
  onChanged: () => void;
}

/**
 * Manage the distributor sheet's category sections: create (name + optional position), rename,
 * reorder (sort_order edit), delete. Delete is blocked while ANY license — archived included —
 * references the section (pre-disabled with the reason; the backend field error is surfaced too if
 * a race slips through). The ManageCategoriesModal precedent.
 */
function SplaCategoriesModal({ isOpen, onClose, onChanged }: SplaCategoriesModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { categories, loading, error, refetch } = useAdminSplaCategories(isOpen);

  const [addDraft, setAddDraft] = useState('');
  const [addOrderDraft, setAddOrderDraft] = useState('');
  const [addServerError, setAddServerError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const addTrimmed = addDraft.trim();
  const addOrderTrimmed = addOrderDraft.trim();
  const isAddNameValid = addTrimmed.length > 0 && addTrimmed.length <= NAME_MAX;
  const isAddOrderValid = addOrderTrimmed === '' || /^\d+$/.test(addOrderTrimmed);
  const addHelperError =
    addServerError ??
    (addTrimmed.length > NAME_MAX
      ? `Name must be at most ${NAME_MAX} characters.`
      : !isAddOrderValid
        ? 'Sort order must be a non-negative whole number.'
        : null);

  const handleClose = () => {
    if (hasChanges) {
      onChanged();
    }
    onClose();
  };

  const updateCategory = async (id: number, changes: SplaCategoryUpdateRequest): Promise<CategoryMutationResult> => {
    try {
      const response = await apiFetch(`/admin/spla/categories/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(changes),
      });
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseSplaCategory | null;
      if (body?.status === 'success' && body.data?.category) {
        addNotification({
          title: 'Section updated',
          srTitle: 'Success alert',
          description: `"${body.data.category.name}" is at position ${body.data.category.sort_order}.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        setHasChanges(true);
        await refetch();
        return { ok: true, fieldError: null };
      }
      const fieldError = body?.data?.name ?? body?.data?.sort_order ?? null;
      addNotification({
        title: 'Could not update the section',
        srTitle: 'Error alert',
        description: fieldError ?? body?.message ?? 'Could not update the section. Please try again.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false, fieldError };
    } catch (err) {
      addNotification({
        title: 'Could not update the section',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false, fieldError: null };
    }
  };

  const deleteCategory = async (id: number): Promise<{ ok: boolean }> => {
    try {
      const response = await apiFetch(`/admin/spla/categories/${id}`, { method: 'DELETE' });
      const body = (await response.json().catch(() => null)) as ApiResponseSplaCategoryDeleted | null;
      if (body?.status === 'success' && body.data?.deleted) {
        addNotification({
          title: 'Section deleted',
          srTitle: 'Success alert',
          description: `The section "${body.data.deleted.name}" was deleted.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        setHasChanges(true);
        await refetch();
        return { ok: true };
      }
      // An in-use delete is rejected with the reason under data.category (the row pre-disables
      // this, but a race is still possible).
      const reason = body?.data?.category ?? body?.message ?? 'Could not delete the section. Please try again.';
      addNotification({
        title: 'Could not delete the section',
        srTitle: 'Error alert',
        description: reason,
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false };
    } catch (err) {
      addNotification({
        title: 'Could not delete the section',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false };
    }
  };

  const handleAdd = async () => {
    if (!isAddNameValid || !isAddOrderValid) {
      return;
    }
    setIsAdding(true);
    setAddServerError(null);
    try {
      const response = await apiFetch('/admin/spla/categories', {
        method: 'POST',
        body: JSON.stringify({
          name: addTrimmed,
          ...(addOrderTrimmed !== '' ? { sort_order: Number(addOrderTrimmed) } : {}),
        }),
      });
      const body = (await response.json().catch(() => null)) as ApiResponseSplaCategory | null;
      if (body?.status === 'success' && body.data?.category) {
        addNotification({
          title: 'Section created',
          srTitle: 'Success alert',
          description: `The section "${body.data.category.name}" was created.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        setHasChanges(true);
        setAddDraft('');
        setAddOrderDraft('');
        await refetch();
      } else {
        const fieldError = body?.data?.name ?? body?.data?.sort_order ?? null;
        setAddServerError(fieldError);
        addNotification({
          title: 'Could not create the section',
          srTitle: 'Error alert',
          description: fieldError ?? body?.message ?? 'Could not create the section. Please try again.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      }
    } catch (err) {
      addNotification({
        title: 'Could not create the section',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsAdding(false);
    }
  };

  const renderBody = () => {
    if (loading && categories.length === 0) {
      return (
        <Flex justifyContent={{ default: 'justifyContentCenter' }} style={{ padding: '2rem' }}>
          <FlexItem>
            <Spinner aria-label="Loading sections" />
          </FlexItem>
        </Flex>
      );
    }

    if (error && categories.length === 0) {
      return (
        <Alert variant="danger" isInline title="Could not load the sections">
          {error}
        </Alert>
      );
    }

    return (
      <>
        <Content component="p" className="pf-v6-u-mb-sm">
          Sections mirror the distributor sheet&apos;s layout; the position number orders them on the sheet.
        </Content>
        <Table aria-label="SPLA sheet sections" variant="compact">
          <Thead>
            <Tr>
              <Th>Section</Th>
              <Th>Position</Th>
              <Th>Usage</Th>
              <Th screenReaderText="Row actions" />
            </Tr>
          </Thead>
          <Tbody>
            {categories.map((category) => (
              <SplaCategoryRow
                key={category.id}
                category={category}
                onUpdate={updateCategory}
                onDelete={deleteCategory}
              />
            ))}
          </Tbody>
        </Table>

        <div className="pf-v6-u-mt-lg">
          <Content component="h3" className="pf-v6-u-mb-sm">
            Add a section
          </Content>
          <Flex alignItems={{ default: 'alignItemsFlexStart' }} spaceItems={{ default: 'spaceItemsSm' }}>
            <FlexItem grow={{ default: 'grow' }}>
              <TextInput
                id="spla-category-add-name"
                type="text"
                value={addDraft}
                onChange={(_event, value) => {
                  setAddDraft(value);
                  if (addServerError) {
                    setAddServerError(null);
                  }
                }}
                validated={addHelperError ? 'error' : 'default'}
                isDisabled={isAdding}
                aria-label="New section name"
                placeholder="Section name"
              />
            </FlexItem>
            <FlexItem>
              <TextInput
                id="spla-category-add-order"
                type="text"
                inputMode="numeric"
                value={addOrderDraft}
                onChange={(_event, value) => {
                  setAddOrderDraft(value);
                  if (addServerError) {
                    setAddServerError(null);
                  }
                }}
                validated={!isAddOrderValid ? 'error' : 'default'}
                isDisabled={isAdding}
                aria-label="New section position (optional)"
                placeholder="Position"
                style={{ width: '6rem' }}
              />
            </FlexItem>
            <FlexItem>
              <Button
                variant="secondary"
                onClick={() => void handleAdd()}
                isLoading={isAdding}
                isDisabled={isAdding || !isAddNameValid || !isAddOrderValid}
              >
                Add
              </Button>
            </FlexItem>
          </Flex>
          {addHelperError && (
            <HelperText>
              <HelperTextItem variant="error">{addHelperError}</HelperTextItem>
            </HelperText>
          )}
        </div>
      </>
    );
  };

  return (
    <Modal variant="medium" isOpen={isOpen} onClose={handleClose} aria-label="Manage SPLA sheet sections">
      <ModalHeader title="Manage categories" />
      <ModalBody tabIndex={0} aria-label="SPLA sheet sections">
        {renderBody()}
      </ModalBody>
      <ModalFooter>
        <Button variant="link" onClick={handleClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default SplaCategoriesModal;
